import type { AppUiConnection, AppUiVersion } from './types';

export const APP_UI_REQUEST_TIMEOUT_MS = 3_000;

/**
 * App V2 is the default UI. Only the API's explicit `main` decision (an
 * opted-out organization, or a non-production stack defaulting to main) loads
 * the retained main frontend; a failed, slow or malformed answer selects V2.
 */
export async function resolveAppUi({
  apiUrl,
  token,
  fetcher = fetch,
  timeoutMs = APP_UI_REQUEST_TIMEOUT_MS,
}: {
  apiUrl: string;
  token?: string;
  fetcher?: typeof fetch;
  timeoutMs?: number;
}): Promise<AppUiVersion> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetcher(
      `${apiUrl.replace(/\/+$/, '')}/auth/app-ui`,
      {
        credentials: 'include',
        cache: 'no-store',
        signal: controller.signal,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (!response.ok) return 'v2';
    const body: unknown = await response.json();
    return body !== null &&
      typeof body === 'object' &&
      'version' in body &&
      body.version === 'main'
      ? 'main'
      : 'v2';
  } catch {
    return 'v2';
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Start the selected UI. If the V2 bundle itself cannot load (a failed chunk,
 * a throwing entry), fall back to the retained main frontend so the app stays
 * usable; a main failure propagates to the boot recovery page.
 * Resolves with the UI that actually rendered.
 */
export async function startAppUi({
  version,
  startV2,
  startMain,
}: {
  version: AppUiVersion;
  startV2: () => Promise<void>;
  startMain: () => Promise<void>;
}): Promise<AppUiVersion> {
  if (version === 'v2') {
    try {
      await startV2();
      return 'v2';
    } catch {
      // Fall through to main.
    }
  }
  await startMain();
  return 'main';
}

/** Recheck only after a real navigation, once the editor's leave guard ran. */
export function connectAppUiRefresh({
  version,
  connection,
  decide,
  currentUrl,
  reload,
  activityRevision = () => 0,
  isBlocked = () => false,
}: {
  version: AppUiVersion;
  connection: AppUiConnection;
  decide: (token?: string) => Promise<AppUiVersion>;
  currentUrl: () => string;
  reload: () => void;
  activityRevision?: () => number;
  isBlocked?: () => boolean;
}): () => void {
  let lastUrl = currentUrl();
  let generation = 0;
  let disposed = false;
  let reloading = false;
  const unsubscribe = connection.onNavigation(() => {
    const url = currentUrl();
    if (url === lastUrl || disposed || reloading) return;
    lastUrl = url;
    const request = ++generation;
    const activity = activityRevision();
    const isSafe = () =>
      !disposed &&
      request === generation &&
      currentUrl() === url &&
      activityRevision() === activity &&
      !isBlocked() &&
      connection.canReload();
    if (!isSafe()) return;
    void decide(connection.getRequestToken())
      .then(async (next) => {
        if (next === version || !isSafe()) return;
        reloading = true;
        await connection.prepareReload();
        if (isSafe()) reload();
        else reloading = false;
      })
      .catch(() => {
        // A failed secure-storage write must not discard a signed-in session.
        // Retry the hand-off on the next navigation instead.
        reloading = false;
      });
  });
  return () => {
    disposed = true;
    generation += 1;
    unsubscribe();
  };
}
